import { PermissionCode, type ManagedSite } from '@gb-ticket/shared-types';
import { Loader2Icon, MoreHorizontalIcon, PlusIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { adminPanelClassName, adminTableFrameClassName } from '@/platform/features/admin-panel';
import {
  SiteFormDialog,
  type SiteFormValues,
} from '@/platform/features/organisation/site-form-dialog';
import { useDialogInvalidationHold } from '@/platform/lib/use-invalidation-hold';
import {
  useCreateSite,
  useDeleteSite,
  useSites,
  useUpdateSite,
} from '@/platform/features/organisation/use-organisation';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';

type DialogState = { mode: 'create' } | { mode: 'edit'; values: SiteFormValues } | null;

type DeactivateState = {
  id: string;
  name: string;
  userCount: number;
} | null;

type DeleteState = {
  id: string;
  name: string;
} | null;

function toFormValues(item: ManagedSite): SiteFormValues {
  return {
    id: item.id,
    name: item.name,
    slug: item.slug,
    isActive: item.isActive,
  };
}

export function SitesPage() {
  const me = useMe();
  const canWrite = hasPermission(me.data, PermissionCode.USERS_MANAGE);
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState<string | undefined>();
  const sites = useSites({ q, includeInactive: true });
  const createSite = useCreateSite();
  const updateSite = useUpdateSite();
  const deleteSite = useDeleteSite();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [deactivate, setDeactivate] = useState<DeactivateState>(null);
  const [pendingDelete, setPendingDelete] = useState<DeleteState>(null);
  useDialogInvalidationHold(dialog !== null || deactivate !== null || pendingDelete !== null);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setQ(searchInput.trim() || undefined);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const busy = createSite.isPending || updateSite.isPending;

  async function handleFormSubmit(payload: {
    create?: Parameters<typeof createSite.mutateAsync>[0];
    update?: Parameters<typeof updateSite.mutateAsync>[0]['body'];
  }) {
    try {
      if (dialog?.mode === 'create' && payload.create) {
        await createSite.mutateAsync(payload.create);
        toast.success('Standort angelegt.');
        setDialog(null);
        return;
      }
      if (dialog?.mode === 'edit' && payload.update && dialog.values.id) {
        await updateSite.mutateAsync({ id: dialog.values.id, body: payload.update });
        toast.success(
          payload.update.isActive === false ? 'Standort deaktiviert.' : 'Standort gespeichert.',
        );
        setDialog(null);
      }
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Standort konnte nicht gespeichert werden.',
      );
    }
  }

  async function confirmDeactivate() {
    if (!deactivate) {
      return;
    }
    try {
      await updateSite.mutateAsync({ id: deactivate.id, body: { isActive: false } });
      toast.success('Standort deaktiviert.');
      setDeactivate(null);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Standort konnte nicht deaktiviert werden.',
      );
    }
  }

  async function reactivate(item: ManagedSite) {
    try {
      await updateSite.mutateAsync({ id: item.id, body: { isActive: true } });
      toast.success('Standort reaktiviert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Standort konnte nicht reaktiviert werden.',
      );
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) {
      return;
    }
    try {
      await deleteSite.mutateAsync(pendingDelete.id);
      toast.success(`„${pendingDelete.name}“ wurde endgültig gelöscht.`);
      setPendingDelete(null);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Standort konnte nicht gelöscht werden.',
      );
    }
  }

  function usageHint(state: DeactivateState): string {
    if (!state) {
      return '';
    }
    if (state.userCount === 0) {
      return `„${state.name}“ wird deaktiviert und erscheint nicht mehr in neuen Zuweisungen.`;
    }
    const people = `${state.userCount} Benutzer`;
    return `„${state.name}“ wird deaktiviert. Betroffen: ${people}. Bestehende Zuordnungen bleiben erhalten.`;
  }

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="Standorte"
        description="Büro oder Einsatzort."
        action={
          canWrite ? (
            <Button type="button" onClick={() => setDialog({ mode: 'create' })}>
              <PlusIcon className="size-4" aria-hidden />
              Standort hinzufügen
            </Button>
          ) : undefined
        }
      />

      <div className={`${adminPanelClassName} max-w-sm`}>
        <Input
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Name oder Kürzel suchen …"
          aria-label="Standorte suchen"
        />
      </div>

      {sites.isPending && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Standorte werden geladen …
        </p>
      )}

      {sites.isError && (
        <p role="alert" className="text-sm text-destructive">
          {sites.error instanceof ApiRequestError
            ? sites.error.message
            : 'Die Standorte konnten nicht geladen werden.'}
        </p>
      )}

      {sites.data && (
        <div className={adminTableFrameClassName}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Kürzel</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Anzahl Personen</TableHead>
                {canWrite && (
                  <TableHead className="w-12">
                    <span className="sr-only">Aktionen</span>
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sites.data.items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={canWrite ? 5 : 4}
                    className="text-center text-muted-foreground"
                  >
                    Keine Standorte gefunden.
                  </TableCell>
                </TableRow>
              ) : (
                sites.data.items.map((item) => (
                  <TableRow key={item.id} className={cn(!item.isActive && 'opacity-70')}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell className="font-mono text-sm">{item.slug}</TableCell>
                    <TableCell>
                      {item.isActive ? (
                        <Badge variant="outline">Aktiv</Badge>
                      ) : (
                        <Badge variant="destructive">Deaktiviert</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{item.userCount}</TableCell>
                    {canWrite && (
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              aria-label={`${item.name}: Aktionen`}
                            >
                              <MoreHorizontalIcon className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onSelect={() =>
                                setDialog({ mode: 'edit', values: toFormValues(item) })
                              }
                            >
                              Bearbeiten
                            </DropdownMenuItem>
                            {item.isActive ? (
                              <DropdownMenuItem
                                variant="destructive"
                                onSelect={() =>
                                  setDeactivate({
                                    id: item.id,
                                    name: item.name,
                                    userCount: item.userCount,
                                  })
                                }
                              >
                                Deaktivieren
                              </DropdownMenuItem>
                            ) : (
                              <>
                                <DropdownMenuItem onSelect={() => void reactivate(item)}>
                                  Reaktivieren
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  variant="destructive"
                                  disabled={!item.canDelete}
                                  title={
                                    item.canDelete
                                      ? undefined
                                      : 'Löschen erst möglich, wenn niemand zugeordnet ist.'
                                  }
                                  onSelect={() =>
                                    setPendingDelete({ id: item.id, name: item.name })
                                  }
                                >
                                  Endgültig löschen
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <SiteFormDialog
        open={dialog !== null}
        mode={dialog?.mode ?? 'create'}
        initial={dialog?.mode === 'edit' ? dialog.values : null}
        busy={busy}
        onOpenChange={(open) => {
          if (!open) {
            setDialog(null);
          }
        }}
        onSubmit={handleFormSubmit}
      />

      <AlertDialog
        open={deactivate !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeactivate(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Standort deaktivieren?</AlertDialogTitle>
            <AlertDialogDescription>{usageHint(deactivate)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateSite.isPending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={updateSite.isPending}
              onClick={(event) => {
                event.preventDefault();
                void confirmDeactivate();
              }}
            >
              {updateSite.isPending ? (
                <>
                  <Loader2Icon className="size-4 animate-spin" aria-hidden />
                  Wird deaktiviert …
                </>
              ) : (
                'Deaktivieren'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Standort endgültig löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete ? `„${pendingDelete.name}“ wird unwiderruflich entfernt.` : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSite.isPending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteSite.isPending}
              onClick={(event) => {
                event.preventDefault();
                void confirmDelete();
              }}
            >
              {deleteSite.isPending ? (
                <>
                  <Loader2Icon className="size-4 animate-spin" aria-hidden />
                  Wird gelöscht …
                </>
              ) : (
                'Endgültig löschen'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
